import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import { getColorFromPercentage } from './colorUtils';

/**
 * Export data as JSON file
 */
export const exportToJSON = (data, filename) => {
  const jsonString = JSON.stringify(data, null, 2);
  const blob = new Blob([jsonString], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename || `report_${new Date().toISOString().split('T')[0]}.json`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

/**
 * Export data as Excel file
 */
export const exportToExcel = (data, filename) => {
  const workbook = XLSX.utils.book_new();
  
  // Create summary sheet
  const summaryData = [
    ['Project Name', data.displayName || data.projectName || ''],
    ['Evaluation Date', data.evaluationDate || ''],
    ['Overall Score', data.overallScore || 'N/A'],
    ['', ''],
    ['Category', 'Average Score', 'Total Requirements', 'Requirements with Score', 'N/A Count']
  ];
  
  if (data.categories && data.categories.length > 0) {
    data.categories.forEach(category => {
      const totalDetails = category.details ? category.details.length : 0;
      const scoredDetails = category.details ? category.details.filter(d => d.score !== 'N/A' && d.score !== 0 && d.score !== null && d.score !== undefined).length : 0;
      const naCount = totalDetails - scoredDetails;
      summaryData.push([
        category.name, 
        category.average || 'N/A',
        totalDetails,
        scoredDetails,
        naCount
      ]);
    });
  }
  
  const summarySheet = XLSX.utils.aoa_to_sheet(summaryData);
  XLSX.utils.book_append_sheet(workbook, summarySheet, 'Summary');
  
  // Create detailed sheets for each category
  if (data.categories && data.categories.length > 0) {
    data.categories.forEach((category, index) => {
      const categoryData = [
        ['Category', category.name],
        ['Average Score', category.average || 'N/A'],
        ['', ''],
        ['Requirement', 'Score', 'Reason']
      ];
      
      if (category.details && category.details.length > 0) {
        category.details.forEach(detail => {
          categoryData.push([
            detail.requirement || '',
            detail.score || 'N/A',
            detail.reason || ''
          ]);
        });
      }
      
      // Add recommendations if available
      if (category.categoryRecommendations) {
        categoryData.push(['', '', '']);
        categoryData.push(['Recommendations', '', category.categoryRecommendations]);
      }
      
      const categorySheet = XLSX.utils.aoa_to_sheet(categoryData);
      const sheetName = category.name.substring(0, 31) || `Category ${index + 1}`;
      XLSX.utils.book_append_sheet(workbook, categorySheet, sheetName);
    });
  }
  
  // Add recommendations sheet if available
  if (data.overallRecommendations || data.keyStrengths || data.keyAreasForImprovement || data.areasForImprovement) {
    const recommendationsData = [];
    
    if (data.keyStrengths && Array.isArray(data.keyStrengths) && data.keyStrengths.length > 0) {
      recommendationsData.push(['Key Strengths', '']);
      data.keyStrengths.forEach(strength => {
        recommendationsData.push(['', strength]);
      });
      recommendationsData.push(['', '']);
    }
    
    if (data.keyAreasForImprovement && Array.isArray(data.keyAreasForImprovement) && data.keyAreasForImprovement.length > 0) {
      recommendationsData.push(['Key Areas for Improvement', '']);
      data.keyAreasForImprovement.forEach(area => {
        recommendationsData.push(['', area]);
      });
      recommendationsData.push(['', '']);
    }
    
    if (data.areasForImprovement && Array.isArray(data.areasForImprovement) && data.areasForImprovement.length > 0) {
      recommendationsData.push(['Areas for Improvement', '']);
      data.areasForImprovement.forEach(area => {
        recommendationsData.push(['', area]);
      });
      recommendationsData.push(['', '']);
    }
    
    if (data.overallRecommendations) {
      recommendationsData.push(['Overall Recommendations', '']);
      // overallRecommendations is a string, not an array
      if (typeof data.overallRecommendations === 'string') {
        recommendationsData.push(['', data.overallRecommendations]);
      } else if (Array.isArray(data.overallRecommendations) && data.overallRecommendations.length > 0) {
        data.overallRecommendations.forEach(rec => {
          recommendationsData.push(['', rec]);
        });
      }
    }
    
    if (recommendationsData.length > 0) {
      const recommendationsSheet = XLSX.utils.aoa_to_sheet(recommendationsData);
      XLSX.utils.book_append_sheet(workbook, recommendationsSheet, 'Recommendations');
    }
  }
  
  const excelFilename = filename || `report_${new Date().toISOString().split('T')[0]}.xlsx`;
  XLSX.writeFile(workbook, excelFilename);
};

/**
 * Export data as PDF file
 */
export const exportToPDF = async (data, filename) => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 20;
  let yPos = margin;
  const lineHeight = 7;
  const maxWidth = pageWidth - 2 * margin;
  
  // Helper function to add a new page if needed
  const checkNewPage = (requiredSpace) => {
    if (yPos + requiredSpace > pageHeight - margin) {
      doc.addPage();
      yPos = margin;
      return true;
    }
    return false;
  };
  
  // Helper function to convert hex color to RGB
  const hexToRgb = (hex) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
      r: parseInt(result[1], 16),
      g: parseInt(result[2], 16),
      b: parseInt(result[3], 16)
    } : { r: 0, g: 0, b: 0 };
  };

  // Helper function to add text with word wrap
  const addText = (text, fontSize = 10, isBold = false, color = null) => {
    doc.setFontSize(fontSize);
    if (isBold) {
      doc.setFont(undefined, 'bold');
    } else {
      doc.setFont(undefined, 'normal');
    }
    if (color) {
      doc.setTextColor(color.r, color.g, color.b);
    } else {
      doc.setTextColor(0, 0, 0);
    }
    
    const lines = doc.splitTextToSize(text, maxWidth);
    lines.forEach((line, index) => {
      checkNewPage(lineHeight);
      doc.text(line, margin, yPos);
      yPos += lineHeight;
    });
    
    doc.setTextColor(0, 0, 0);
    doc.setFont(undefined, 'normal');
  };

  // Helper function to draw a progress bar
  const drawProgressBar = (x, y, width, height, percentage, color) => {
    const rgb = hexToRgb(color);
    const fillWidth = (width * percentage) / 100;
    
    // Background
    doc.setFillColor(233, 236, 239);
    doc.rect(x, y, width, height, 'F');
    
    // Fill
    doc.setFillColor(rgb.r, rgb.g, rgb.b);
    doc.rect(x, y, fillWidth, height, 'F');
    
    // Border
    doc.setDrawColor(200, 200, 200);
    doc.rect(x, y, width, height, 'S');
  };

  // Helper function to draw a gauge-like visual (using filled shapes)
  const drawGauge = (x, y, width, height, percentage, color) => {
    const rgb = hexToRgb(color);
    const radius = width / 2;
    
    // Draw background (semi-circle representation using rectangle)
    doc.setFillColor(233, 236, 239);
    doc.rect(x - radius, y, width, height, 'F');
    
    // Draw filled portion
    const fillWidth = (width * percentage) / 100;
    doc.setFillColor(rgb.r, rgb.g, rgb.b);
    doc.rect(x - radius, y, fillWidth, height, 'F');
    
    // Draw border
    doc.setDrawColor(200, 200, 200);
    doc.rect(x - radius, y, width, height, 'S');
  };
  
  // Title - centered and renamed
  const projectName = data.displayName || data.projectName || 'Project';
  const titleText = `${projectName} AI Code Quality Dashboards`;
  doc.setFontSize(18);
  doc.setFont(undefined, 'bold');
  doc.setTextColor(0, 0, 0);
  const titleTextWidth = doc.getTextWidth(titleText);
  doc.text(titleText, pageWidth / 2 - titleTextWidth / 2, yPos);
  yPos += 8;
  
  // Project Info
  doc.setFontSize(12);
  doc.setFont(undefined, 'bold');
  doc.text(`Project: ${data.displayName || data.projectName || 'N/A'}`, margin, yPos);
  yPos += lineHeight;
  
  doc.setFontSize(12);
  doc.setFont(undefined, 'bold');
  doc.text(`Evaluation Date: ${data.evaluationDate || 'N/A'}`, margin, yPos);
  yPos += 10;

  // Overall Score Gauge
  const maxScore = 5.0;
  const isNA = data.overallScore === 'N/A' || data.overallScore === null || data.overallScore === undefined;
  const numericScore = isNA ? 0 : (typeof data.overallScore === 'number' ? data.overallScore : parseFloat(data.overallScore) || 0);
  const percentage = numericScore ? (numericScore / maxScore) * 100 : 0;
  const gaugeColor = isNA ? '#95a5a6' : getColorFromPercentage(percentage);
  
  checkNewPage(60);
  
  // Center the title
  doc.setFontSize(14);
  doc.setFont(undefined, 'bold');
  doc.setTextColor(0, 0, 0);
  const scoreTitleText = 'Overall Project Score';
  const scoreTitleTextWidth = doc.getTextWidth(scoreTitleText);
  doc.text(scoreTitleText, pageWidth / 2 - scoreTitleTextWidth / 2, yPos);
  yPos += 15; // Increased margin between title and gauge
  
  const gaugeX = pageWidth / 2;
  const gaugeY = yPos;
  const gaugeWidth = 80;
  const gaugeHeight = 15;
  
  drawGauge(gaugeX, gaugeY, gaugeWidth, gaugeHeight, percentage, gaugeColor);
  
  // Add score text above gauge
  doc.setFontSize(16);
  doc.setFont(undefined, 'bold');
  doc.setTextColor(0, 0, 0);
  const scoreText = isNA ? 'N/A' : numericScore.toFixed(1);
  const scoreTextWidth = doc.getTextWidth(scoreText);
  doc.text(scoreText, gaugeX - scoreTextWidth / 2, gaugeY - 5);
  
  doc.setFontSize(10);
  doc.setFont(undefined, 'normal');
  const labelText = `out of ${maxScore}`;
  const labelTextWidth = doc.getTextWidth(labelText);
  doc.text(labelText, gaugeX - labelTextWidth / 2, gaugeY + gaugeHeight + 8);
  
  if (!isNA) {
    // Make percent text much bigger
    doc.setFontSize(24);
    doc.setFont(undefined, 'bold');
    const percentText = `${percentage.toFixed(1)}%`;
    const percentTextWidth = doc.getTextWidth(percentText);
    doc.text(percentText, gaugeX - percentTextWidth / 2, gaugeY + gaugeHeight + 20);
  }
  
  yPos = gaugeY + gaugeHeight + 30;
  yPos += 10;
  
  // Categories Overview with Progress Bars
  if (data.categories && data.categories.length > 0) {
    checkNewPage(30);
    addText('Categories Performance Overview', 14, true);
    yPos += 8;
    
    data.categories.forEach((category, index) => {
      checkNewPage(15);
      const isCategoryNA = category.average === 'N/A' || category.average === null || category.average === undefined;
      const categoryScore = isCategoryNA ? 0 : (typeof category.average === 'number' ? category.average : parseFloat(category.average) || 0);
      const categoryPercentage = categoryScore ? (categoryScore / 5) * 100 : 0;
      const barColor = isCategoryNA ? '#95a5a6' : getColorFromPercentage(categoryPercentage);
      
      // Category name (truncate if too long)
      const categoryName = category.name.length > 40 ? category.name.substring(0, 37) + '...' : category.name;
      doc.setFontSize(9);
      doc.text(categoryName, margin, yPos);
      
      // Progress bar
      const barX = margin;
      const barY = yPos + 3;
      const barWidth = maxWidth - 50;
      const barHeight = 6;
      drawProgressBar(barX, barY, barWidth, barHeight, categoryPercentage, barColor);
      
      // Score text
      const scoreText = isCategoryNA ? 'N/A' : categoryScore.toFixed(1);
      doc.setFontSize(9);
      doc.text(scoreText, barX + barWidth + 5, yPos + 6);
      
      yPos += 12;
    });
    yPos += 5;
  }
  
  // Categories Details
  if (data.categories && data.categories.length > 0) {
    data.categories.forEach((category, index) => {
      checkNewPage(lineHeight * 5);
      yPos += 5;
      addText(`Category ${index + 1}: ${category.name}`, 14, true);
      addText(`Average Score: ${category.average || 'N/A'}`, 12);
      yPos += 3;
      
      if (category.details && category.details.length > 0) {
        category.details.forEach((detail, detailIndex) => {
          checkNewPage(lineHeight * 4);
          addText(`${detailIndex + 1}. ${detail.requirement}`, 10, true);
          addText(`   Score: ${detail.score || 'N/A'}`, 10);
          addText(`   Reason: ${detail.reason || ''}`, 10);
          yPos += 2;
        });
      }
      
      if (category.categoryRecommendations) {
        checkNewPage(lineHeight * 3);
        addText('Recommendations:', 10, true);
        addText(category.categoryRecommendations, 10);
        yPos += 3;
      }
    });
  }
  
  // Overall Recommendations
  if (data.keyStrengths || data.keyAreasForImprovement || data.areasForImprovement || data.overallRecommendations) {
    checkNewPage(lineHeight * 5);
    yPos += 5;
    addText('Overall Recommendations', 14, true);
    yPos += 3;
    
    if (data.keyStrengths && Array.isArray(data.keyStrengths) && data.keyStrengths.length > 0) {
      addText('Key Strengths:', 12, true);
      data.keyStrengths.forEach(strength => {
        addText(`• ${strength}`, 10);
      });
      yPos += 2;
    }
    
    if (data.keyAreasForImprovement && Array.isArray(data.keyAreasForImprovement) && data.keyAreasForImprovement.length > 0) {
      addText('Key Areas for Improvement:', 12, true);
      data.keyAreasForImprovement.forEach(area => {
        addText(`• ${area}`, 10);
      });
      yPos += 2;
    }
    
    if (data.areasForImprovement && Array.isArray(data.areasForImprovement) && data.areasForImprovement.length > 0) {
      addText('Areas for Improvement:', 12, true);
      data.areasForImprovement.forEach(area => {
        addText(`• ${area}`, 10);
      });
      yPos += 2;
    }
    
    if (data.overallRecommendations) {
      addText('Overall Recommendations:', 12, true);
      // overallRecommendations is a string, not an array
      if (typeof data.overallRecommendations === 'string') {
        addText(data.overallRecommendations, 10);
      } else if (Array.isArray(data.overallRecommendations) && data.overallRecommendations.length > 0) {
        data.overallRecommendations.forEach(rec => {
          addText(`• ${rec}`, 10);
        });
      }
    }
  }
  
  const pdfFilename = filename || `report_${new Date().toISOString().split('T')[0]}.pdf`;
  doc.save(pdfFilename);
};

